import React, { useState } from 'react';
import {
  Box,
  Typography,
  Container,
  Grid,
  Link,
  IconButton,
  Divider,
  TextField,
  Button,
  Alert,
  CircularProgress
} from '@mui/material';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';
import { useBotDefense } from '../../hooks/useBotDefense';
import { motion } from 'framer-motion';
import GitHubIcon from '@mui/icons-material/GitHub';
import InstagramIcon from '@mui/icons-material/Instagram';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import YouTubeIcon from '@mui/icons-material/YouTube';
import TelegramIcon from '@mui/icons-material/Telegram';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { CONTACT_INFO } from '../../constants';
import PrivacyPolicyDialog from './PrivacyPolicyDialog';
import TermsOfServiceDialog from './TermsOfServiceDialog';

// Helper: detects if a background string represents a dark color
const isDarkColor = (color) => {
  if (!color || typeof color !== 'string') return true;
  const c = color.trim().toLowerCase();
  if (
    c.includes('18, 18, 18') ||
    c.includes('30, 41, 59') ||
    c.includes('15, 23, 42') ||
    c.includes('10, 14, 23') ||
    c.includes('11, 24, 39') ||
    c.includes('17, 24, 39') ||
    c === '#000' ||
    c === '#000000' ||
    c === '#111827' ||
    c === '#121212' ||
    c === '#1e1e1e' ||
    c === '#1e293b' ||
    c === '#0f172a' ||
    c === '#0a0e17' ||
    c === '#0b0f19'
  ) return true;
  if (c.startsWith('#')) {
    const hex = c.replace('#', '');
    let r = 0, g = 0, b = 0;
    if (hex.length === 3) {
      r = parseInt(hex[0] + hex[0], 16);
      g = parseInt(hex[1] + hex[1], 16);
      b = parseInt(hex[2] + hex[2], 16);
    } else if (hex.length >= 6) {
      r = parseInt(hex.substring(0, 2), 16);
      g = parseInt(hex.substring(2, 4), 16);
      b = parseInt(hex.substring(4, 6), 16);
    }
    return (0.2126 * r + 0.7152 * g + 0.0722 * b) < 128;
  }
  const rgbMatch = c.match(/rgba?\((\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
  if (rgbMatch) {
    const r = parseInt(rgbMatch[1], 10);
    const g = parseInt(rgbMatch[2], 10);
    const b = parseInt(rgbMatch[3], 10);
    return (0.2126 * r + 0.7152 * g + 0.0722 * b) < 128;
  }
  return false;
};

const Footer = () => {
  const { isDarkMode } = useTheme();
  const { t, contactInfo, isSectionVisible, newsletterUi } = useLanguage();
  const { executeBotDefense, isBotDefenseActive } = useBotDefense();
  const activeContact = contactInfo || CONTACT_INFO;
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);

  // Footer Newsletter State
  const [footerEmail, setFooterEmail] = useState('');
  const [footerLoading, setFooterLoading] = useState(false);
  const [footerFeedback, setFooterFeedback] = useState(null);

  const showFooterNewsletter =
    isSectionVisible('newsletter') &&
    (newsletterUi?.show_in_footer === true ||
      newsletterUi?.placement === 'footer' ||
      newsletterUi?.placement === 'both');

  const handleFooterSubscribe = async (e) => {
    e.preventDefault();
    if (!footerEmail || !footerEmail.includes('@')) {
      setFooterFeedback({ type: 'error', message: t('emailInvalid') || 'Please enter a valid email address.' });
      return;
    }

    setFooterLoading(true);
    setFooterFeedback(null);

    let botToken = null;
    if (isBotDefenseActive) {
      try {
        botToken = await executeBotDefense('newsletter');
      } catch (e) {}
    }

    try {
      const response = await fetch('/api/newsletter.php?action=subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: footerEmail.trim(),
          bot_token: botToken,
          hp_newsletter: '' // honeypot
        })
      });

      const data = await response.json();
      if (data.success) {
        setFooterFeedback({
          type: 'success',
          message: data.message || t('newsletterSuccess') || 'Thank you for subscribing!'
        });
        setFooterEmail('');
      } else {
        setFooterFeedback({
          type: 'error',
          message: data.error || 'Failed to subscribe. Please try again.'
        });
      }
    } catch (err) {
      setFooterFeedback({
        type: 'error',
        message: 'Network error. Please try again later.'
      });
    } finally {
      setFooterLoading(false);
    }
  };

  const handlePrivacyOpen = (e) => {
    e.preventDefault();
    setPrivacyOpen(true);
  };

  const handlePrivacyClose = () => {
    setPrivacyOpen(false);
  };

  const handleTermsOpen = (e) => {
    e.preventDefault();
    setTermsOpen(true);
  };

  const handleTermsClose = () => {
    setTermsOpen(false);
  };
  
  return (
    <Box
      component="footer"
      sx={{
        py: 6,
        pb: 'calc(6 * 8px + env(safe-area-inset-bottom))',
        backgroundColor: isDarkMode ? 'rgba(18, 18, 18, 0.95)' : 'rgba(46, 91, 255, 0.03)',
        borderTop: '1px solid',
        borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Typography
                variant="h6"
                component="div"
                sx={{
                  fontWeight: 700,
                  letterSpacing: '0.5px',
                  mb: 2,
                  background: 'linear-gradient(45deg, #2E5BFF, #00C896)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  display: 'inline-block',
                }}
              >
                ITFORSEC
              </Typography>
              
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 3 }}
              >
                {t('footerDesc')}
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {[
                  { key: 'linkedin', label: 'LinkedIn', icon: <LinkedInIcon fontSize="small" />, url: activeContact.linkedin },
                  { key: 'facebook', label: 'Facebook', icon: <FacebookIcon fontSize="small" />, url: activeContact.facebook },
                  { key: 'instagram', label: 'Instagram', icon: <InstagramIcon fontSize="small" />, url: activeContact.instagram },
                  { key: 'github', label: 'GitHub', icon: <GitHubIcon fontSize="small" />, url: activeContact.github },
                  { key: 'twitter', label: 'Twitter / X', icon: <TwitterIcon fontSize="small" />, url: activeContact.twitter },
                  { key: 'youtube', label: 'YouTube', icon: <YouTubeIcon fontSize="small" />, url: activeContact.youtube },
                  { key: 'telegram', label: 'Telegram', icon: <TelegramIcon fontSize="small" />, url: activeContact.telegram },
                ]
                  .filter(item => {
                    if (!item.url || !item.url.trim()) return false;
                    if (activeContact.social_visibility && activeContact.social_visibility[item.key] === false) {
                      return false;
                    }
                    return true;
                  })
                  .map(item => (
                    <IconButton
                      key={item.key}
                      size="small"
                      component="a"
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={item.label}
                      sx={{
                        color: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
                        '&:hover': {
                          color: theme => theme.palette.primary.main,
                          backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
                        }
                      }}
                    >
                      {item.icon}
                    </IconButton>
                  ))}
              </Box>

              {/* Newsletter Subscription in Footer - Adaptive Light/Dark Theme */}
              {showFooterNewsletter && (
                <Box
                  sx={{
                    mt: 2.5,
                    p: 2,
                    borderRadius: 2.5,
                    backgroundColor: isDarkMode
                      ? (newsletterUi?.bg_color || 'rgb(18, 18, 18)')
                      : (newsletterUi?.bg_color_light || (isDarkColor(newsletterUi?.bg_color) ? '#ffffff' : (newsletterUi?.bg_color || '#ffffff'))),
                    border: '1px solid',
                    borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.12)' : 'rgba(46, 91, 255, 0.14)',
                    boxShadow: isDarkMode
                      ? '0 10px 25px rgba(0, 0, 0, 0.5)'
                      : '0 6px 20px rgba(46, 91, 255, 0.08), 0 1px 3px rgba(0, 0, 0, 0.04)',
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'all 0.3s ease',
                  }}
                >
                  {/* Subtle top glow line */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '2px',
                      background: 'linear-gradient(90deg, #2E5BFF 0%, #00C896 100%)'
                    }}
                  />

                  <Typography
                    variant="caption"
                    component="div"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.75,
                      fontWeight: 700,
                      fontSize: '0.78rem',
                      letterSpacing: '0.4px',
                      textTransform: 'uppercase',
                      color: isDarkMode ? '#ffffff' : '#0f172a',
                      mb: 0.5
                    }}
                  >
                    <MarkEmailReadOutlinedIcon sx={{ fontSize: 16, color: '#00C896' }} />
                    {t('newsletterBadge') || 'Stay Updated & Secure'}
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: '0.76rem',
                      color: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : '#475569',
                      mb: 1.5,
                      lineHeight: 1.35
                    }}
                  >
                    {t('newsletterSubtitle') || 'Get exclusive cybersecurity briefings and technology insights directly.'}
                  </Typography>

                  {footerFeedback && (
                    <Alert
                      severity={footerFeedback.type}
                      icon={footerFeedback.type === 'success' ? <CheckCircleOutlineIcon fontSize="small" /> : undefined}
                      sx={{
                        mb: 1.5,
                        py: 0.25,
                        px: 1,
                        fontSize: '0.75rem',
                        '& .MuiAlert-message': { py: 0.25 }
                      }}
                      onClose={() => setFooterFeedback(null)}
                    >
                      {footerFeedback.message}
                    </Alert>
                  )}

                  <Box
                    component="form"
                    onSubmit={handleFooterSubscribe}
                    noValidate
                    sx={{
                      display: 'flex',
                      gap: 1,
                      alignItems: 'center'
                    }}
                  >
                    <input
                      type="text"
                      name="hp_newsletter"
                      tabIndex="-1"
                      autoComplete="off"
                      style={{ display: 'none' }}
                      aria-hidden="true"
                    />

                    <TextField
                      fullWidth
                      size="small"
                      type="email"
                      placeholder={t('newsletterPlaceholder') || 'Enter your email...'}
                      value={footerEmail}
                      onChange={(e) => setFooterEmail(e.target.value)}
                      disabled={footerLoading}
                      required
                      sx={{
                        '& .MuiInputBase-root': {
                          height: '36px',
                          fontSize: '0.8rem',
                          backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.06)' : '#f8fafc',
                          borderRadius: 1.5,
                          '& fieldset': {
                            borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.12)',
                          },
                          '&:hover fieldset': {
                            borderColor: '#2E5BFF',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#2E5BFF',
                            borderWidth: '1.5px',
                          },
                        },
                        '& .MuiInputBase-input': {
                          color: isDarkMode ? '#ffffff' : '#0f172a',
                          py: 0.5,
                          px: 1.25,
                          '&::placeholder': {
                            color: isDarkMode ? 'rgba(255, 255, 255, 0.5)' : '#94a3b8',
                            opacity: 1,
                          },
                        }
                      }}
                    />

                    <Button
                      type="submit"
                      variant="contained"
                      disabled={footerLoading}
                      sx={{
                        minWidth: '42px',
                        width: '42px',
                        height: '36px',
                        p: 0,
                        borderRadius: 1.5,
                        background: 'linear-gradient(135deg, #2E5BFF 0%, #00C896 100%)',
                        color: '#ffffff',
                        boxShadow: '0 2px 8px rgba(46, 91, 255, 0.3)',
                        flexShrink: 0,
                        '&:hover': {
                          background: 'linear-gradient(135deg, #254EDB 0%, #00B084 100%)',
                          boxShadow: '0 4px 12px rgba(46, 91, 255, 0.45)',
                        }
                      }}
                      title={t('newsletterButton') || 'Subscribe'}
                      aria-label={t('newsletterButton') || 'Subscribe'}
                    >
                      {footerLoading ? (
                        <CircularProgress size={18} sx={{ color: '#ffffff' }} />
                      ) : (
                        <SendIcon sx={{ fontSize: 16 }} />
                      )}
                    </Button>
                  </Box>

                  {newsletterUi?.show_disclaimer !== false && (
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        mt: 1,
                        fontSize: '0.68rem',
                        color: isDarkMode ? 'rgba(255, 255, 255, 0.55)' : '#64748b',
                        lineHeight: 1.2
                      }}
                    >
                      {t('newsletterDisclaimer') || '🔒 Zero spam guarantee. 1-click unsubscribe.'}
                    </Typography>
                  )}
                </Box>
              )}
            </motion.div>
          </Grid>
          
          <Grid item xs={6} md={2}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <Typography
                variant="subtitle1"
                component="h6"
                fontWeight={600}
                sx={{ mb: 2 }}
              >
                {t('quickLinks')}
              </Typography>
              
              <Box component="ul" sx={{ p: 0, m: 0, listStyle: 'none' }}>
                {['home', 'services', 'about', 'features', 'testimonials', 'faq', 'contact']
                  .filter(item => {
                    const keyMap = { home: 'hero', services: 'services', about: 'about', features: 'features', testimonials: 'testimonials', faq: 'faq', contact: 'contact' };
                    return !isSectionVisible || isSectionVisible(keyMap[item] || item);
                  })
                  .map((item) => (
                  <Box component="li" key={item} sx={{ mb: 1 }}>
                    <Link
                      href={`#${item}`}
                      underline="hover"
                      color="text.secondary"
                      sx={{
                        display: 'inline-block',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          color: theme => theme.palette.primary.main,
                          transform: 'translateX(3px)',
                        }
                      }}
                    >
                      {t(item) || item}
                    </Link>
                  </Box>
                ))}
              </Box>
            </motion.div>
          </Grid>
          
          <Grid item xs={6} md={2}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <Typography
                variant="subtitle1"
                component="h6"
                fontWeight={600}
                sx={{ mb: 2 }}
              >
                {t('servicesTitle')}
              </Typography>
              
              <Box component="ul" sx={{ p: 0, m: 0, listStyle: 'none' }}>
                {['service1', 'service2', 'service6', 'service8', 'service9'].map((item) => (
                  <Box component="li" key={item} sx={{ mb: 1 }}>
                    <Link
                      href="#services"
                      underline="hover"
                      color="text.secondary"
                      sx={{
                        display: 'inline-block',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          color: theme => theme.palette.primary.main,
                          transform: 'translateX(3px)',
                        }
                      }}
                    >
                      {t(item)}
                    </Link>
                  </Box>
                ))}
              </Box>
            </motion.div>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <Typography
                variant="subtitle1"
                component="h6"
                fontWeight={600}
                sx={{ mb: 2 }}
              >
                {t('contactInfo')}
              </Typography>
              
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 1 }}
              >
                Email: {activeContact.email}
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 3 }}
              >
                Phone: {activeContact.phone}
              </Typography>
              
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  backgroundColor: isDarkMode ? 'rgba(46, 91, 255, 0.05)' : 'rgba(46, 91, 255, 0.03)',
                  border: '1px solid',
                  borderColor: isDarkMode ? 'rgba(46, 91, 255, 0.1)' : 'rgba(46, 91, 255, 0.08)',
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {t('whatsappPrompt')}
                </Typography>
              </Box>
            </motion.div>
          </Grid>
        </Grid>
        
        <Divider sx={{ my: 4, borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)' }} />
        
        <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
             {t('copyright')}
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 3, mt: { xs: 2, sm: 0 } }}>
            <Link
              href="#"
              onClick={handlePrivacyOpen}
              underline="hover"
              color="text.secondary"
              variant="body2"
            >
              {t('privacyPolicy')}
            </Link>
            <Link
              href="#"
              onClick={handleTermsOpen}
              underline="hover"
              color="text.secondary"
              variant="body2"
            >
              {t('termsOfService')}
            </Link>
          </Box>
        </Box>
      </Container>
      
      <PrivacyPolicyDialog
        open={privacyOpen}
        onClose={handlePrivacyClose}
        isDarkMode={isDarkMode}
      />
      
      <TermsOfServiceDialog
        open={termsOpen}
        onClose={handleTermsClose}
        isDarkMode={isDarkMode}
      />
    </Box>
  );
};

export default Footer;
